<template>
    <div
        v-bind="$attrs"
        class="document-description"
        :class="{
            'with-header-background': showHeaderBackground,
            'with-padded-header': props.padHeaderWithoutBackground && !showHeaderBackground,
        }"
        ref="documentDescriptionRef"
    >
        <img
            v-if="shouldProbeHeaderBackground"
            class="header-background-probe"
            :src="props.headerBackgroundImageUrl ?? ''"
            alt=""
            @load="handleHeaderBackgroundLoad"
            @error="handleHeaderBackgroundError"
        />
        <div v-if="showHeaderBackground" class="header-background-image">
            <img :src="props.headerBackgroundImageUrl ?? ''" alt="" />
        </div>
        <div class="header-section">
            <div class="info">
                <div class="title">{{ props.title }}</div>
                <div class="header-controls">
                    <slot name="header-controls" />
                    <document-access-tag :access-reason="props.accessReason" class="access-tag ctrl-buttons" :shared-text="props.sharedText" />
                    <primevue-button
                        v-if="!props.hasAccess && !authState.loggedIn"
                        class="get-access-button"
                        @click="showContactFormModal = true"
                    >
                        Get access to this report
                        <font-awesome-icon :icon="['fas', 'envelope']" />
                    </primevue-button>
                    <report-bookmark-button v-if="props.allowBookmark !== false" :report-id="props.reportId" />
                    <primevue-button v-if="canEditResearchDocument" text class="plain-btn ctrl-buttons" @click="openEdit">
                        Edit
                        <font-awesome-icon :icon="['fas', 'pencil']" />
                    </primevue-button>
                </div>
            </div>
            <div class="subtitle" v-if="props.subtitle">
                {{props.subtitle}}
            </div>
        </div>
        <div v-if="tagsToShow.length > 0" class="category">
            <button
                v-for="tag in tagsToShow"
                :key="tag.id"
                class="header-tag-button"
                :class="{ clickable: tag.clickable, 'ctrl-buttons': !!tag.filterId }"
                type="button"
                :aria-disabled="!tag.clickable"
                :tabindex="tag.clickable ? 0 : -1"
                @click="handleTagClick(tag)"
            >
                <primevue-tag
                    :class="{ clickable: tag.clickable }"
                    :value="tag.name"
                    :severity="tag.severity"
                />
            </button>
        </div>
        <slot name="after-tags" />
        <div class="details" v-if="props.authors.length > 0">
            <div class="detail" v-for="(author, index) in props.authors" :key="index">
                <font-awesome-icon :icon="['fas', 'users']" />
                <span>{{ author }}</span>
            </div>
        </div>
        <div class="details" v-if="props.numberOfPages">
            <div class="detail">
                <font-awesome-icon :icon="['fas', 'book']" />
                <span>{{ props.numberOfPages }} pages</span>
            </div>
        </div>
        <div class="details" v-if="formattedPublicationDate">
            <div class="detail">
                <font-awesome-icon :icon="['fas', 'calendar-days']" />
                <span>{{ formattedPublicationDate }}</span>
            </div>
        </div>
        <div class="bottom-row">
            <div class="description" v-html="sanitizedDescription" />
            <div class="buttons-holder" v-if="props.hasAccess">
                <template v-if="props.allowZoom">
                    <primevue-button text class="plain-btn ctrl-buttons" @click="makeFullScreen">
                        Full screen
                        <font-awesome-icon :icon="['fas', 'expand']" />
                    </primevue-button>
                    <primevue-button text class="plain-btn ctrl-buttons" @click="zoomIn">
                        Zoom in
                        <font-awesome-icon :icon="['fas', 'magnifying-glass-plus']" />
                    </primevue-button>
                    <primevue-button text class="plain-btn ctrl-buttons" @click="zoomOut">
                        Zoom out
                        <font-awesome-icon :icon="['fas', 'magnifying-glass-minus']" />
                    </primevue-button>
                </template>
                <primevue-button v-else-if="props.reportType === 'PowerBi'" text class="plain-btn ctrl-buttons" @click="makeFullScreen">
                    Full screen
                    <font-awesome-icon :icon="['fas', 'expand']" />
                </primevue-button>
            </div>
        </div>
    </div>
    <contact-form v-model:visible="showContactFormModal" prefill-text="I would like access to this report." />
</template>

<script lang="ts" setup>
import { authState } from '@lcp/http-auth';
import DocumentAccessTag from '@/components/common/DocumentAccessTag.vue';
import ContactForm from '@/components/deltaGpt/ContactForm.vue';
import ReportBookmarkButton from '@/components/deltaGpt/ReportBookmarkButton.vue';
import primevueButton from 'primevue/button';
import PrimevueTag from 'primevue/tag';
import { IDocumentFilterNew, ReportType } from '@/interfaces/deltaGpt/IDocument.ts';
import { router } from '@/router.ts';
import { useDeltaUserSettingsStore } from '@/stores/deltaGpt/deltaGptUserSettingsStore.ts';
import { computed, ref, watch } from 'vue';
import { IIdNameBase } from '@/interfaces/general/IIdName.ts';
import { getIdNames } from '@/utils/documentFiltersUtils.ts';
import { useRoute } from 'vue-router';
import { getCurrentSectionResearch, getResearchSectionRouteName } from '@/routes/routeUtils.ts';
import { SectionRouteNamesResearch } from '@/routes/sectionRoutes/sectionRouteNames.ts';
import { deltaGptRoutes } from '@/routes/deltaGptRoutes.ts';
import { TAccessReason } from '@/types/general/TAccess.ts';
import { sanitizeRichTextHtml } from '@/utils/htmlUtils.ts';
import { useUserAccessStore } from '@/stores/userAccessStore.ts';
import { DEFAULT_TIMEZONE } from '@/constants/Time.ts';
import { parsePublicationDate } from '@/utils/sparkContentUtils.ts';

interface IReportHeaderTag {
    id: string;
    name: string;
    severity?: 'secondary' | 'success' | 'info' | 'warn' | 'danger' | 'contrast';
    clickable?: boolean;
    filterId?: string;
    routeName?: string;
}

type TReportDetailsEditMode = 'admin' | 'local' | 'hidden';

const deltaUserSettingsStore = useDeltaUserSettingsStore();
const userAccessStore = useUserAccessStore();
const showContactFormModal = ref<boolean>(false);

const props = defineProps<{
    allowZoom?: boolean;
    hasAccess: boolean;
    description?: string | null;
    numberOfPages?: number | null;
    publicationDate: string;
    authors: Array<string>;
    filters: Array<IDocumentFilterNew>;
    title: string;
    subtitle?: string | null;
    sharedText?: string | undefined;
    accessReason: TAccessReason;
    reportType: ReportType;
    reportId: string;
    allowBookmark?: boolean;
    headerBackgroundImageUrl?: string | null;
    alternativeTags?: Array<IReportHeaderTag>;
    editMode?: TReportDetailsEditMode;
    padHeaderWithoutBackground?: boolean;
}>();

const emit = defineEmits<{
    'makeFullScreen': [void];
    'zoomIn': [void];
    'zoomOut': [void];
    'edit': [void];
    'headerTagClick': [IReportHeaderTag];
}>();

const route = useRoute();

const currentRouteSection = computed<SectionRouteNamesResearch>(() => getCurrentSectionResearch(route));
const currentEditMode = computed<TReportDetailsEditMode>(() => props.editMode ?? 'admin');
const canEditResearchDocument = computed<boolean>(() => (
    currentEditMode.value !== 'hidden'
    && (userAccessStore.userAccess.isAdmin || userAccessStore.userAccess.research.admin)
));
const headerBackgroundStatus = ref<'pending' | 'loaded' | 'failed'>('pending');
const tagsToShow = computed<Array<IReportHeaderTag>>(() => {
    if (props.alternativeTags?.length) return props.alternativeTags;

    return getIdNames(props.filters).map((filter: IIdNameBase) => ({
        id: `filter-${filter.id}`,
        name: filter.name,
        clickable: true,
        filterId: filter.id,
    }));
});
const shouldProbeHeaderBackground = computed<boolean>(() => !!props.headerBackgroundImageUrl && headerBackgroundStatus.value === 'pending');
const showHeaderBackground = computed<boolean>(() => !!props.headerBackgroundImageUrl && headerBackgroundStatus.value === 'loaded');
const formattedPublicationDate = computed<string>(() => {
    const parsedPublicationDate = parsePublicationDate(props.publicationDate);
    if (!parsedPublicationDate) return '';
    const parsedDate = parsedPublicationDate.toDate();

    if (props.reportType === 'Webinar') {
        return parsedDate.toLocaleDateString('en-gb', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            weekday: undefined,
            hour: '2-digit',
            minute: '2-digit',
            timeZone: DEFAULT_TIMEZONE, // Note: if we want to add the ability to add webinars in different timezones, we will need to add support here.
            timeZoneName: 'short',
        });
    }

    return parsedDate.toLocaleDateString('en-gb', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        weekday: undefined,
    });
});

const sanitizedDescription = computed<string>(() => sanitizeRichTextHtml(props.description));

function navigateToSearch (filterId: string): void {
    deltaUserSettingsStore.selectedDocumentFilterPaths[currentRouteSection.value] = [filterId];

    const toRouteTo = getResearchSectionRouteName(route, deltaGptRoutes.documentList.name);

    router.push({ name: toRouteTo });
}

function openEdit (): void {
    if (currentEditMode.value === 'local') {
        emit('edit');
        return;
    }

    const resolvedRoute = router.resolve({
        name: 'researchAdminContent',
        query: {
            docId: props.reportId,
        },
    });

    window.open(resolvedRoute.href, '_blank', 'noopener,noreferrer');
}

function zoomIn (): void {
    emit('zoomIn');
}

function zoomOut (): void {
    emit('zoomOut');
}

function makeFullScreen (): void {
    emit('makeFullScreen');
}

function handleTagClick (tag: IReportHeaderTag): void {
    if (!tag.clickable) return;
    if (tag.filterId) {
        navigateToSearch(tag.filterId);
        return;
    }
    emit('headerTagClick', tag);
}

function handleHeaderBackgroundLoad (): void {
    headerBackgroundStatus.value = 'loaded';
}

function handleHeaderBackgroundError (): void {
    headerBackgroundStatus.value = 'failed';
}

watch(() => props.headerBackgroundImageUrl, () => {
    headerBackgroundStatus.value = 'pending';
});

</script>

<style lang="scss" scoped>
.document-description {
    grid-area: header;
    position: relative;
    overflow: hidden;
    padding-bottom: var(--extraLargeSpacing);
    display: flex;
    flex-direction: column;
    gap: var(--extraLargeSpacing);

    .ctrl-buttons {
        border: 1px solid var(--neutral400);
    }

    .title {
        font-size: var(--extraLargeFontSize);
        font-weight: var(--boldFontWeight);
        position: relative;
    }
    .header-section {
        display: flex;
        flex-direction: column;
        gap: var(--smallSpacing);

        .subtitle {
            font-size: var(--smallFontSize);
            color: var(--neutral800);
            font-style: italic;
            line-height: 1.5;
            margin-top: calc(var(--smallSpacing) * -1);
        }
    }

    .details {
        gap: var(--defaultSpacing);
        display: flex;
        flex-direction: column;

        .detail {
            display: flex;
            gap: var(--smallSpacing);
        }
    }

    .info {
        gap: var(--largeSpacing);
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        .access-tag {
            font-size: var(--smallFontSize);
            margin-left: auto;
            :deep(.colour-icon) {
                width: 1.2rem;
                height: 1.2rem;
            }
        }

    }

    .header-controls {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: var(--smallSpacing);
        margin-left: auto;
    }

    .category {
        display: flex;
        gap: var(--smallSpacing);
        flex-wrap: wrap;

        .header-tag-button {
            border: 0;
            background: transparent;
            padding: 0;

            &[aria-disabled='true'] {
                cursor: default;
            }

            &.clickable {
                cursor: pointer;
            }
        }

    }

    .bottom-row {
        display: flex;
        flex-wrap: wrap;
        gap: var(--largeSpacing);
        @include responsive('sm') {
            flex-direction: column;
        }
        .description {
            flex: 1;
            border-left: 0.5rem solid var(--accentColour);
            padding-left: var(--defaultSpacing);
            :deep(ul) {
                list-style-type: disc;
                list-style-position: inside;
                padding-left: var(--defaultSpacing);
            }
            :deep(li) {
                margin-top: var(--smallSpacing);
            }
            :deep(p) {
                margin-bottom: var(--defaultSpacing);
            }
        }
        .buttons-holder {
            flex: 0 0 auto;
            display: flex;
            gap: var(--defaultSpacing);
            align-self: flex-end;

        }
    }

}

.header-background-probe {
    display: none;
}

.header-background-image {
    position: absolute;
    inset: 0;
    z-index: 0;
    overflow: hidden;
    background: var(--neutral200);

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        background:
            linear-gradient(
                to right,
                var(--neutral0) 0%,
                color-mix(in srgb, var(--neutral0) 96%, transparent) 28%,
                color-mix(in srgb, var(--neutral0) 78%, transparent) 48%,
                transparent 78%
            ),
            linear-gradient(to bottom, transparent 0%, transparent 58%, var(--neutral0) 100%);
        pointer-events: none;
    }

    img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
    }
}

.with-header-background {
    min-height: clamp(18rem, 38vh, 28rem);
    justify-content: flex-end;
    padding: var(--largeSpacing);

    .header-section {
        max-width: 100%;
    }

    .info {
        width: 100%;
        align-items: flex-start;
    }

    .header-controls {
        position: absolute;
        top: var(--largeSpacing);
        right: var(--largeSpacing);
        z-index: 2;
    }

    .title {
        max-width: min(48rem, calc(100% - 24rem));
    }

    .subtitle,
    .category,
    .details,
    .bottom-row {
        max-width: min(48rem, 100%);
    }

    > :not(.header-background-image):not(.header-background-probe):not(.header-section) {
        position: relative;
        z-index: 1;
    }

    > .header-section {
        position: static;
        z-index: auto;
    }

    .title,
    .subtitle {
        position: relative;
        z-index: 1;
    }

    @include responsive('sm') {
        .info {
            flex-direction: column;
            align-items: stretch;
        }

        .header-controls {
            position: static;
            width: 100%;
            max-width: 100%;
            margin-left: 0;
            justify-content: flex-start;
            flex-wrap: wrap;
        }

        .title,
        .subtitle,
        .category,
        .details,
        .bottom-row {
            max-width: 100%;
        }
    }
}

.with-padded-header {
    padding: var(--largeSpacing);
}
</style>
